import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  AccountTree,
  CheckCircle,
  Add,
  SwapHoriz,
  CloudUpload,
  Description,
} from "@mui/icons-material";
import {
  TransitionGraphService,
  MatchGraphResponse,
  NodeMatch,
  TransitionInfo,
  RegisterTransitionsResponse,
} from "../services/transitionGraphService";
import { SSTFApiClient, GraphQLTestTarget, TestTargetType } from "../services/api";
import { operationLogService } from "../services/operationLogService";
import { buildDefaultItemParams } from "../utils/itemParams";
import { getViewPaths, setViewPaths } from "../utils/viewPathUtils";

type ViewAction = "existing" | "manual" | "skipped" | "bulk_create";
type ItemAction = "existing" | "manual" | "skipped" | "bulk_create";

interface ViewMatchResolution extends NodeMatch {
  action: ViewAction;
  parentServiceId?: string;
}

interface TransitionResolution extends TransitionInfo {
  action: ItemAction;
  resolved_item_test_target_id?: string;
  resolved_item_name?: string;
  source_view_graph_node_id?: string;
  source_view_name?: string;
  source_view_test_target_id?: string;
}

interface NormalizedTarget {
  id: string;
  name: string;
  type: string;
  parentId?: string;
}

interface TransitionGraphDialogProps {
  open: boolean;
  onClose: () => void;
  onRegistrationComplete: () => void;
  workspaceId: string;
  projectId: string;
  transitionGraphService: TransitionGraphService;
  apiClient: SSTFApiClient | null;
}

export const TransitionGraphDialog: React.FC<TransitionGraphDialogProps> = ({
  open,
  onClose,
  onRegistrationComplete,
  workspaceId,
  projectId,
  transitionGraphService,
  apiClient,
}) => {
  const [graphText, setGraphText] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matchResult, setMatchResult] = useState<MatchGraphResponse | null>(
    null
  );
  const [registering, setRegistering] = useState(false);
  const [viewMatches, setViewMatches] = useState<ViewMatchResolution[]>([]);
  const [transitionResolutions, setTransitionResolutions] = useState<
    TransitionResolution[]
  >([]);
  const [existingTargets, setExistingTargets] = useState<NormalizedTarget[]>([]);
  const [typeMap, setTypeMap] = useState<Record<string, string>>({});
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [generatingTransitions, setGeneratingTransitions] = useState(false);
  const [viewSelectionState, setViewSelectionState] = useState<{
    nodeId: string | null;
    requestedAction: ViewAction | null;
  }>({ nodeId: null, requestedAction: null });
  const [itemSelectionState, setItemSelectionState] = useState<{
    index: number | null;
    requestedAction: ItemAction | null;
  }>({ index: null, requestedAction: null });
  const [bulkProcessingViews, setBulkProcessingViews] = useState(false);
  const [bulkProcessingItems, setBulkProcessingItems] = useState(false);
  const [viewSelectModalOpen, setViewSelectModalOpen] = useState(false);
  const [itemSelectModalOpen, setItemSelectModalOpen] = useState(false);
  const [itemCreateModalOpen, setItemCreateModalOpen] = useState(false);
  const [updatedViewPaths, setUpdatedViewPaths] = useState<Set<string>>(new Set());
  const [viewDataCache, setViewDataCache] = useState<Map<string, GraphQLTestTarget>>(new Map());
  const [selectedServiceIdForView, setSelectedServiceIdForView] = useState("");
  const [selectedExistingViewId, setSelectedExistingViewId] = useState("");
  const [selectedServiceIdForItem, setSelectedServiceIdForItem] = useState("");
  const [selectedViewIdForItem, setSelectedViewIdForItem] = useState("");
  const [selectedItemIdForExisting, setSelectedItemIdForExisting] = useState("");
  const [newViewName, setNewViewName] = useState("");
  const [newItemName, setNewItemName] = useState("");
  const [newServiceNameForView, setNewServiceNameForView] = useState("");
  const [isViewChanged, setIsViewChanged] = useState(false);
  const [serviceCreateModalOpen, setServiceCreateModalOpen] = useState(false);
  const [isCreatingNewViewInManualSelect, setIsCreatingNewViewInManualSelect] =
    useState(false);
  /** 手動選択ダイアログで新規作成したViewのid/name。作成後に「適用」でマッチング結果に反映するまで保持する */
  const [manuallyCreatedViewForMatch, setManuallyCreatedViewForMatch] =
    useState<{ viewId: string; viewName: string } | null>(null);
  const [isCreatingNewViewInItemSelect, setIsCreatingNewViewInItemSelect] =
    useState(false);
  const [isCreatingNewItemInManualSelect, setIsCreatingNewItemInManualSelect] =
    useState(false);
  const [transitionRegistrationResult, setTransitionRegistrationResult] =
    useState<RegisterTransitionsResponse | null>(null);
  const [serviceCreateReturnTarget, setServiceCreateReturnTarget] = useState<
    "view" | "item" | null
  >(null);
  const [regenCooldown, setRegenCooldown] = useState(false);
  const lastMatchResultRef = useRef<MatchGraphResponse | null>(null);
  /** 適用時にindexのずれを防ぐため、現在の transitionResolutions を ref で参照する */
  const transitionResolutionsRef = useRef<TransitionResolution[]>([]);
  /** 手動で選択で開いた遷移行を一意に識別するキーと、同キー行の何番目か。effect で配列が並び替わっても正しい行に適用するため */
  const itemSelectionKeyRef = useRef<{ key: string; indexInKeyGroup: number } | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

  const normalizeTargets = useCallback(
    (targets: GraphQLTestTarget[]): NormalizedTarget[] =>
      targets.map((target) => ({
        id: target.testTargetId,
        name: target.name,
        type: target.type?.name || "",
        parentId: target.parentTestTargetId || "",
      })),
    []
  );

  const ensureTargetType = useCallback(
    (targets: NormalizedTarget[], fallbackType: string): NormalizedTarget[] =>
      targets.map((target) => ({
        ...target,
        type: target.type || fallbackType,
      })),
    []
  );

  const parseMermaidEdges = useCallback(() => {
    const edges: {
      sourceId: string;
      sourceLabel: string;
      targetId: string;
      targetLabel: string;
      edgeLabel: string;
    }[] = [];

    const nodeInfo = (raw: string) => {
      const trimmed = raw.trim();
      const match = trimmed.match(/^([A-Za-z0-9_-]+)\s*(?:[\[(](.+)[\])])?/);
      const id = match?.[1]?.trim() || trimmed;
      const label = (match?.[2] || match?.[1] || "").trim();
      return { id, label };
    };

    graphText
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.includes("-->"))
      .forEach((line) => {
        const match = line.match(/([^-\s][^>]*)-->\s*\|?([^|>]*)\|?\s*([^\s].*)/);
        if (!match) {
          return;
        }
        const [, rawSource, rawEdgeLabel, rawTarget] = match;
        const source = nodeInfo(rawSource);
        const target = nodeInfo(rawTarget);
        edges.push({
          sourceId: source.id,
          sourceLabel: source.label || source.id,
          targetId: target.id,
          targetLabel: target.label || target.id,
          edgeLabel: (rawEdgeLabel || "").trim(),
        });
      });

    return edges;
  }, [graphText]);

  const extractMermaidGraph = useCallback((content: string): string | null => {
    if (!content) {
      return null;
    }

    const mermaidMatch = content.match(/```mermaid\s*([\s\S]*?)```/i);
    const fromMermaid = mermaidMatch?.[1]?.trim() ?? "";

    const fallbackStart = content.toLowerCase().indexOf("graph td");
    const fallback = fallbackStart !== -1 ? content.slice(fallbackStart).trim() : "";

    const candidate = fromMermaid || fallback;
    if (!candidate.toLowerCase().includes("graph td")) {
      return null;
    }
    return candidate;
  }, []);

  // 同名・別Viewの行を区別するため遷移元（source）を含める
  const buildTransitionKey = (
    trans: TransitionInfo | TransitionResolution
  ): string => {
    const r = trans as TransitionResolution & { source_view_id?: string };
    const sourceKey =
      r.source_view_test_target_id ||
      r.source_view_id ||
      r.source_view_name ||
      r.source_view_graph_node_id ||
      "";
    return [
      trans.item_test_target_id || "",
      trans.item_name,
      trans.target_view_id,
      trans.edge_label || "",
      sourceKey,
    ].join("::");
  };

  const enrichTransitionsWithSource = useCallback(
    (
      transitions: TransitionResolution[],
      matches: ViewMatchResolution[]
    ): TransitionResolution[] => {
      const edges = parseMermaidEdges();
      const sourceMatches = matches;
      const viewNameToId = new Map(
        sourceMatches.map((m) => [
          m.graph_node_name.trim(),
          m.view_test_target_id || "",
        ])
      );
      const viewIdByGraphId = new Map(
        sourceMatches.map((m) => [m.graph_node_id, m.view_test_target_id || ""])
      );

      return transitions.map((trans) => {
        const targetName = (trans.target_view_name || "").trim();
        const edgeLabelNeedle = (trans.edge_label || trans.item_name || "").trim();

        const candidate =
          edges.find((edge) => {
            const targetMatch =
              edge.targetLabel === targetName ||
              edge.targetId === targetName ||
              edge.targetLabel === trans.target_view_id ||
              edge.targetId === trans.target_view_id;
            const edgeLabel = edge.edgeLabel.trim();
            const labelMatch =
              (!!edgeLabelNeedle && edgeLabel === edgeLabelNeedle) ||
              (!edgeLabelNeedle && !!edgeLabel);
            return targetMatch && labelMatch;
          }) ||
          edges.find((edge) => {
            const targetMatch =
              edge.targetLabel === targetName ||
              edge.targetId === targetName ||
              edge.targetLabel === trans.target_view_id ||
              edge.targetId === trans.target_view_id;
            return targetMatch;
          });

        const sourceName =
          trans.source_view_name ||
          candidate?.sourceLabel ||
          candidate?.sourceId ||
          "";
        const sourceIdFromName = sourceName
          ? viewNameToId.get(sourceName.trim()) || ""
          : "";
        const sourceIdFromGraphId = candidate?.sourceId
          ? viewIdByGraphId.get(candidate.sourceId) || ""
          : "";
        const sourceId =
          trans.source_view_test_target_id || sourceIdFromName || sourceIdFromGraphId;

        return {
          ...trans,
          source_view_graph_node_id:
            trans.source_view_graph_node_id || candidate?.sourceId,
          source_view_name: sourceName || trans.source_view_name,
          source_view_test_target_id: sourceId,
        };
      });
    },
    [parseMermaidEdges]
  );

  const fetchTargets = useCallback(async () => {
    if (!apiClient || !workspaceId || !projectId) {
      return;
    }
    setLoadingTargets(true);
    setSelectionError(null);
    try {
      const [targets, types] = await Promise.all([
        apiClient.getTestTargets(workspaceId, projectId),
        apiClient.getTestTargetTypes(workspaceId, projectId),
      ]);
      setExistingTargets(normalizeTargets(targets));
      const map: Record<string, string> = {};
      types.forEach((typeInfo: TestTargetType) => {
        if (typeInfo?.name && typeInfo.typeId) {
          map[typeInfo.name.toLowerCase()] = typeInfo.typeId;
        }
      });
      setTypeMap(map);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "ターゲット情報の取得に失敗しました";
      setSelectionError(message);
    } finally {
      setLoadingTargets(false);
    }
  }, [apiClient, normalizeTargets, projectId, workspaceId]);

  useEffect(() => {
    if (open) {
      void fetchTargets();
    }
  }, [fetchTargets, open]);

  const services = useMemo(
    () =>
      existingTargets.filter(
        (target) => target.type.toLowerCase() === "service"
      ),
    [existingTargets]
  );

  const TEMP_SERVICE_NAME = "仮Service（遷移情報一括登録処理から自動生成）";

  const getDefaultParentServiceForBulkCreate = useCallback((): string | null => {
    if (services.length === 0) {
      return null;
    }
    return services[0].id;
  }, [services]);

  const views = useMemo(
    () =>
      existingTargets.filter((target) => target.type.toLowerCase() === "view"),
    [existingTargets]
  );

  const items = useMemo(
    () =>
      existingTargets.filter((target) => target.type.toLowerCase() === "item"),
    [existingTargets]
  );

  const hasServiceType = useMemo(() => Boolean(typeMap.service), [typeMap]);

  const viewsByService = useMemo(() => {
    const map = new Map<string, NormalizedTarget[]>();
    views.forEach((view) => {
      const list = map.get(view.parentId || "") || [];
      list.push(view);
      map.set(view.parentId || "", list);
    });
    return map;
  }, [views]);

  const itemsByView = useMemo(() => {
    const map = new Map<string, NormalizedTarget[]>();
    items.forEach((item) => {
      const list = map.get(item.parentId || "") || [];
      list.push(item);
      map.set(item.parentId || "", list);
    });
    return map;
  }, [items]);

  const targetById = useMemo(() => {
    const map = new Map<string, NormalizedTarget>();
    existingTargets.forEach((target) => {
      map.set(target.id, target);
    });
    return map;
  }, [existingTargets]);

  const hasBulkCreateViews = useMemo(
    () => viewMatches.some((match) => match.action === "bulk_create"),
    [viewMatches]
  );

  const hasBulkCreateItems = useMemo(
    () => transitionResolutions.some((trans) => trans.action === "bulk_create"),
    [transitionResolutions]
  );

  // 更新可能なpathがあるViewを判定
  const hasUpdatablePaths = useMemo(() => {
    if (!apiClient) {
      return false;
    }
    return viewMatches.some((match) => {
      // actionが"existing"で、view_test_target_idとpathが存在する場合
      if (match.action !== "existing" || !match.view_test_target_id || !match.path) {
        return false;
      }
      // 既に更新済みのViewは除外
      const updateKey = `${match.view_test_target_id}:${match.path}`;
      if (updatedViewPaths.has(updateKey)) {
        return false;
      }
      // キャッシュからViewデータを取得
      const existingView = viewDataCache.get(match.view_test_target_id);
      if (!existingView) {
        // キャッシュにない場合は更新可能と判定（実際の更新時に再チェック）
        return true;
      }
      // 既存のparamsを取得
      let existingParams: any = {};
      try {
        existingParams = existingView.params
          ? typeof existingView.params === "string"
            ? JSON.parse(existingView.params)
            : existingView.params
          : {};
      } catch (e) {
        // パースエラーの場合は更新可能と判定
        return true;
      }
      // 既存のpathsを取得（pathとpathsの両方を考慮）
      const currentPaths = getViewPaths(existingParams);
      // ノードのpathが既存のpathsに含まれているかチェック
      const hasPath = currentPaths.some((p) => p.path === match.path);
      // 含まれていない場合のみ更新可能
      return !hasPath;
    });
  }, [viewMatches, apiClient, updatedViewPaths, viewDataCache]);

  const findServiceIdForTarget = useCallback(
    (targetId?: string) => {
      if (!targetId) {
        return "";
      }
      let current: NormalizedTarget | undefined = targetById.get(targetId);
      while (current) {
        if (current.type.toLowerCase() === "service") {
          return current.id;
        }
        current = current.parentId ? targetById.get(current.parentId) : undefined;
      }
      return "";
    },
    [targetById]
  );

  // ダイアログが開かれたときに状態をリセット
  useEffect(() => {
    if (open) {
      setGraphText("");
      setLoading(false);
      setError(null);
      setMatchResult(null);
      setRegistering(false);
      setViewMatches([]);
      setTransitionResolutions([]);
      setSelectionError(null);
      setGeneratingTransitions(false);
      setIsViewChanged(false);
      setViewSelectionState({ nodeId: null, requestedAction: null });
      setItemSelectionState({ index: null, requestedAction: null });
      setViewSelectModalOpen(false);
      setItemSelectModalOpen(false);
      setItemCreateModalOpen(false);
      setSelectedServiceIdForView("");
      setSelectedExistingViewId("");
      setSelectedServiceIdForItem("");
      setSelectedViewIdForItem("");
      setSelectedItemIdForExisting("");
      setNewViewName("");
      setNewItemName("");
      setNewServiceNameForView("");
      setServiceCreateModalOpen(false);
      setFileError(null);
      setIsDragOver(false);
      setUploadedFileName(null);
      setIsCreatingNewViewInManualSelect(false);
      setIsCreatingNewViewInItemSelect(false);
      setIsCreatingNewItemInManualSelect(false);
      setServiceCreateReturnTarget(null);
      itemSelectionKeyRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    transitionResolutionsRef.current = transitionResolutions;
  }, [transitionResolutions]);

  // マッチング結果をステートに反映
  useEffect(() => {
    if (!matchResult) {
      return;
    }

    let mergedViewMatches: ViewMatchResolution[] = [];

    // Viewマッチの反映（既存の操作を優先）
    setViewMatches((prev) => {
      const previousViewMap = new Map(
        prev.map((match) => [match.graph_node_id, match] as const)
      );
      const defaultParent = getDefaultParentServiceForBulkCreate();
      const merged = matchResult.matches.map((match) => {
        const existing = previousViewMap.get(match.graph_node_id);
        if (existing) {
          // NOTE: 手動選択を優先。既存のview_test_target_id/view_nameがある場合は分析結果で上書きしない
          return {
            ...match,
            action: existing.action,
            view_test_target_id:
              existing.view_test_target_id ?? match.view_test_target_id,
            view_name: existing.view_name ?? match.view_name,
            parentServiceId:
              existing.parentServiceId ??
              (match.view_test_target_id
                ? findServiceIdForTarget(match.view_test_target_id)
                : defaultParent || undefined),
          };
        }
        const action: ViewAction = match.view_test_target_id ? "existing" : "bulk_create";
        return {
          ...match,
          action,
          parentServiceId:
            match.view_test_target_id && findServiceIdForTarget(match.view_test_target_id)
              ? findServiceIdForTarget(match.view_test_target_id)
              : defaultParent || undefined,
        };
      });
      mergedViewMatches = merged;
      return merged;
    });

    // 遷移情報の反映（既存の操作を優先しつつ情報を更新）
    setTransitionResolutions((prev) => {
      const previousMap = new Map(
        prev.map((trans) => [buildTransitionKey(trans), trans] as const)
      );

      const updatedTransitions: TransitionResolution[] = matchResult.transitions.map(
        (trans) => {
          const key = buildTransitionKey(trans);
          const existing = previousMap.get(key);
          const action: ItemAction =
            existing?.action ??
            (trans.item_test_target_id && trans.item_test_target_id.length > 0
              ? "existing"
              : "bulk_create");
          return {
            ...trans,
            action,
            resolved_item_test_target_id:
              existing?.resolved_item_test_target_id ?? trans.item_test_target_id,
            resolved_item_name: existing?.resolved_item_name ?? trans.item_name,
          };
        }
      );

      const confidencePriority = (confidence: string) => {
        const normalized = confidence?.toLowerCase?.() || "";
        if (normalized === "high") {
          return 3;
        }
        if (normalized === "medium") {
          return 2;
        }
        if (normalized === "low") {
          return 1;
        }
        return 0;
      };

      const dedupKey = (trans: TransitionResolution) => {
        const resolvedId = (trans.resolved_item_test_target_id || "").trim();
        const itemId = (trans.item_test_target_id || "").trim();
        const labelKey = (trans.edge_label || trans.item_name || "").trim();
        const sourceKey =
          trans.source_view_test_target_id ||
          (trans as TransitionResolution & { source_view_id?: string })
            .source_view_id ||
          trans.source_view_name ||
          trans.source_view_graph_node_id ||
          "";
        const identityKey = resolvedId || itemId || labelKey;
        return [identityKey, trans.target_view_id, sourceKey].join("::");
      };

      const dedupedMap = new Map<string, TransitionResolution>();

      updatedTransitions.forEach((trans) => {
        const key = dedupKey(trans);
        const existing = dedupedMap.get(key);
        const hasResolvedItem = Boolean(
          trans.resolved_item_test_target_id || trans.item_test_target_id
        );

        if (!existing) {
          dedupedMap.set(key, trans);
          return;
        }

        const existingHasResolvedItem = Boolean(
          existing.resolved_item_test_target_id || existing.item_test_target_id
        );

        // 既存Itemに紐づくエントリを優先し、次点で信頼度の高い方を残す
        if (hasResolvedItem && !existingHasResolvedItem) {
          dedupedMap.set(key, trans);
          return;
        }
        if (hasResolvedItem === existingHasResolvedItem) {
          const currentPriority = confidencePriority(trans.confidence);
          const existingPriority = confidencePriority(existing.confidence);
          if (currentPriority > existingPriority) {
            dedupedMap.set(key, trans);
          }
        }
      });

      const dedupedTransitions = Array.from(dedupedMap.values());

      return enrichTransitionsWithSource(dedupedTransitions, mergedViewMatches);
    });
    setIsViewChanged(false);

    if (lastMatchResultRef.current !== matchResult) {
      lastMatchResultRef.current = matchResult;
    }
  }, [enrichTransitionsWithSource, findServiceIdForTarget, getDefaultParentServiceForBulkCreate, matchResult]);

  useEffect(() => {
    if (!matchResult) {
      return;
    }
    setTransitionResolutions((prev) =>
      enrichTransitionsWithSource(prev, viewMatches)
    );
  }, [matchResult, viewMatches, enrichTransitionsWithSource]);

  // マッチング実行処理
  const handleMatch = async () => {
    if (!graphText.trim()) {
      setError("グラフテキストを入力してください");
      return;
    }

    if (!workspaceId || !projectId) {
      setError("ワークスペースIDとプロジェクトIDが必要です");
      return;
    }

    // マッチング実行時に更新済みの記録をリセット
    setUpdatedViewPaths(new Set());
    // Viewデータキャッシュを再取得
    if (apiClient) {
      try {
        const targets = await apiClient.getTestTargets(workspaceId, projectId);
        const viewCache = new Map<string, GraphQLTestTarget>();
        targets.forEach((target) => {
          if (target.type?.name?.toLowerCase() === "view") {
            viewCache.set(target.testTargetId, target);
          }
        });
        setViewDataCache(viewCache);
      } catch (error) {
        console.warn("Viewデータキャッシュの更新に失敗:", error);
      }
    }

    setLoading(true);
    setError(null);
    setMatchResult(null);
    setSelectionError(null);

    try {
      const result = await transitionGraphService.matchGraphWithTargets({
        graphText: graphText.trim(),
        workspaceId,
        projectId,
      });

      setMatchResult(result);
    } catch (error: any) {
      console.error("グラフマッチングエラー:", error);
      setError(`グラフマッチングに失敗しました: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const processFile = useCallback(
    (file: File, onFinally?: () => void) => {
      if (!file.name.toLowerCase().endsWith(".md")) {
        setFileError(".md形式のファイルのみアップロードできます");
        setUploadedFileName(null);
        onFinally?.();
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const text = typeof reader.result === "string" ? reader.result : "";
        const graph = extractMermaidGraph(text);
        if (!graph) {
          setFileError(
            "遷移情報の記載が見つかりませんでした。ファイルに「graph TD」から開始する内容が記載されているか確認してください。"
          );
          setUploadedFileName(null);
          onFinally?.();
          return;
        }
        setFileError(null);
        setGraphText(graph);
        setError(null);
        setUploadedFileName(file.name);
        onFinally?.();
      };
      reader.onerror = () => {
        setFileError("ファイルの読み込みに失敗しました");
        setUploadedFileName(null);
        onFinally?.();
      };
      reader.readAsText(file);
    },
    [extractMermaidGraph]
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      const resetInput = () => {
        event.target.value = "";
      };

      if (!file) {
        return;
      }

      processFile(file, resetInput);
    },
    [processFile]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);
      const file = event.dataTransfer.files?.[0];
      if (!file) {
        return;
      }
      processFile(file);
    },
    [processFile]
  );

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleGenerateTransitions = async () => {
    if (!graphText.trim()) {
      setError("グラフテキストを入力してください");
      return;
    }
    if (!workspaceId || !projectId) {
      setError("ワークスペースIDとプロジェクトIDが必要です");
      return;
    }

    setGeneratingTransitions(true);
    setError(null);
    try {
      const result = await transitionGraphService.matchGraphWithTargets({
        graphText: graphText.trim(),
        workspaceId,
        projectId,
        overrideMatches: viewMatches,
        traceId: matchResult?.trace_id,  // 再生成時は既存のtrace_idを継続
      });
      setMatchResult(result);
    } catch (err: unknown) {
      console.error("遷移情報生成エラー:", err);
      const message =
        err instanceof Error ? err.message : "遷移情報の生成に失敗しました";
      setError(`遷移情報の生成に失敗しました: ${message}`);
    } finally {
      setGeneratingTransitions(false);
    }
  };

  // チェックボックスの変更処理
  const applyViewResolution = (
    graphNodeId: string,
    action: ViewAction,
    viewId?: string,
    viewName?: string,
    parentServiceId?: string
  ) => {
    setIsViewChanged(true);
    setViewMatches((prev) =>
      prev.map((match) =>
        match.graph_node_id === graphNodeId
          ? {
              ...match,
              action,
              view_test_target_id: action === "skipped" ? undefined : viewId,
              view_name: action === "skipped" ? undefined : viewName,
              confidence: action === "skipped" ? match.confidence : "manual",
              // NOTE: 手動で新規作成したViewはまだexistingTargetsにないため、明示的に渡したparentServiceIdを優先する
              parentServiceId:
                action === "bulk_create"
                  ? parentServiceId ?? match.parentServiceId
                  : viewId
                  ? parentServiceId ?? findServiceIdForTarget(viewId)
                  : parentServiceId ?? match.parentServiceId,
              reason:
                action === "skipped"
                  ? match.reason
                  : action === "manual"
                  ? "ユーザーが手動選択"
                  : "分析結果を使用",
            }
          : match
      )
    );
  };

  const applyItemResolution = (
    index: number,
    action: ItemAction,
    itemId?: string,
    itemName?: string
  ) => {
    setTransitionResolutions((prev) =>
      prev.map((trans, idx) =>
        idx === index
          ? {
              ...trans,
              action,
              resolved_item_test_target_id:
                action === "skipped" || action === "bulk_create" ? undefined : itemId,
              resolved_item_name:
                action === "skipped" || action === "bulk_create"
                  ? undefined
                  : itemName,
            }
          : trans
      )
    );
  };

  const resolveViewIdForTransition = useCallback(
    (trans: TransitionResolution): string => {
      if (trans.source_view_test_target_id) {
        return trans.source_view_test_target_id;
      }
      if (trans.source_view_name) {
        const hit = views.find((v) => v.name === trans.source_view_name);
        if (hit) {
          return hit.id;
        }
      }
      if (trans.source_view_graph_node_id) {
        const match = viewMatches.find(
          (m) => m.graph_node_id === trans.source_view_graph_node_id
        );
        if (match?.view_test_target_id) {
          return match.view_test_target_id;
        }
      }
      return "";
    },
    [viewMatches, views]
  );

  const handleViewActionChange = (graphNodeId: string, action: ViewAction) => {
    const targetMatch = viewMatches.find(
      (match) => match.graph_node_id === graphNodeId
    );
    if (!targetMatch) {
      return;
    }
    if (action === "manual") {
      // TASK: 手動選択に切り替えた時点でactionをmanualに更新し、プルダウン表示を「手動で選択」にする
      applyViewResolution(
        graphNodeId,
        "manual",
        targetMatch.view_test_target_id,
        targetMatch.view_name
      );
      const defaultServiceId =
        findServiceIdForTarget(targetMatch.view_test_target_id) ||
        selectedServiceIdForView ||
        (services.length > 0 ? services[0].id : "");
      setSelectedServiceIdForView(defaultServiceId);
      const candidateViews = viewsByService.get(defaultServiceId) || [];
      setSelectedExistingViewId(
        candidateViews.length > 0 ? candidateViews[0].id : ""
      );
      setViewSelectionState({ nodeId: graphNodeId, requestedAction: action });
      setIsCreatingNewViewInManualSelect(false);
      setNewViewName(targetMatch.graph_node_name);
      setViewSelectModalOpen(true);
      return;
    }
    if (action === "bulk_create") {
      const parentId =
        findServiceIdForTarget(targetMatch.view_test_target_id) ||
        targetMatch.parentServiceId ||
        getDefaultParentServiceForBulkCreate() ||
        undefined;
      applyViewResolution(
        graphNodeId,
        action,
        targetMatch.view_test_target_id,
        targetMatch.view_name,
        parentId
      );
      return;
    }
    applyViewResolution(
      graphNodeId,
      action,
      targetMatch.view_test_target_id,
      targetMatch.view_name
    );
  };

  const handleItemActionChange = (index: number, action: ItemAction) => {
    const targetTrans = transitionResolutions[index];
    if (!targetTrans) {
      return;
    }
    if (action === "manual") {
      // 手動選択に切り替えた時点でactionをmanualに更新し、プルダウン表示を「手動で選択」にする
      applyItemResolution(
        index,
        "manual",
        targetTrans.resolved_item_test_target_id,
        targetTrans.resolved_item_name
      );
      // NOTE: effectで配列が並び替わっても適用先を特定するため、行の安定キーと同キー内の序数をrefに保持
      const key = buildTransitionKey(targetTrans);
      const indexInKeyGroup = transitionResolutions
        .slice(0, index)
        .filter((t) => buildTransitionKey(t) === key).length;
      itemSelectionKeyRef.current = { key, indexInKeyGroup };
      const resolvedSourceViewId = resolveViewIdForTransition(targetTrans);
      const defaultViewId = resolvedSourceViewId || targetTrans.target_view_id || "";
      const defaultServiceId =
        findServiceIdForTarget(defaultViewId) ||
        selectedServiceIdForItem ||
        "";
      setSelectedServiceIdForItem(defaultServiceId);
      setSelectedViewIdForItem(defaultViewId);
      const candidateItems = itemsByView.get(defaultViewId) || [];
      setSelectedItemIdForExisting(
        candidateItems.length > 0 ? candidateItems[0].id : ""
      );
      setItemSelectionState({ index, requestedAction: action });
      setIsCreatingNewViewInItemSelect(false);
      setIsCreatingNewItemInManualSelect(false);
      setNewItemName(targetTrans.item_name);
      setItemSelectModalOpen(true);
      return;
    }
    if (action === "bulk_create") {
      applyItemResolution(index, action);
      return;
    }
    applyItemResolution(
      index,
      action,
      targetTrans.resolved_item_test_target_id,
      targetTrans.resolved_item_name
    );
  };

  const handleViewSelected = (
    viewId: string,
    viewName: string,
    parentServiceId?: string
  ) => {
    if (!viewSelectionState.nodeId) {
      return;
    }
    applyViewResolution(
      viewSelectionState.nodeId,
      viewSelectionState.requestedAction || "manual",
      viewId,
      viewName,
      parentServiceId
    );
    setViewSelectionState({ nodeId: null, requestedAction: null });
    setViewSelectModalOpen(false);
  };

  const handleItemSelected = (itemId: string, itemName: string) => {
    const keyRef = itemSelectionKeyRef.current;
    const indexFromState = itemSelectionState.index;
    const indexToApply =
      keyRef != null
        ? (() => {
            const current = transitionResolutionsRef.current;
            const indices = current
              .map((t, i) => (buildTransitionKey(t) === keyRef.key ? i : -1))
              .filter((i) => i >= 0);
            return indices[keyRef.indexInKeyGroup] ?? indices[0];
          })()
        : indexFromState;
    if (indexToApply === undefined || indexToApply === null) {
      return;
    }
    applyItemResolution(
      indexToApply,
      itemSelectionState.requestedAction || "manual",
      itemId,
      itemName
    );
    itemSelectionKeyRef.current = null;
    setItemSelectionState({ index: null, requestedAction: null });
    setItemSelectModalOpen(false);
  };

  const createView = async (params?: {
    serviceId?: string;
    viewName?: string;
    path?: string;
  }): Promise<GraphQLTestTarget | null> => {
    if (!apiClient) {
      setSelectionError("APIクライアントが初期化されていません");
      return null;
    }
    if (!typeMap.view) {
      setSelectionError("Viewタイプの取得に失敗しています");
      return null;
    }
    const targetServiceId = params?.serviceId ?? selectedServiceIdForView;
    if (!targetServiceId) {
      setSelectionError("Serviceを選択してください");
      return null;
    }
    const targetViewName = (params?.viewName ?? newViewName) || "新規View";

    try {
      // paramsオブジェクトを構築
      const viewParams: any = { status: "need_review" };
      
      // path情報がある場合はpathsに追加
      if (params?.path) {
        viewParams.paths = [{ path: params.path }];
      }

      const created = await apiClient.createTestTargets(
        workspaceId,
        projectId,
        [
          {
            name: targetViewName,
            displayOrder: 1,
            typeId: typeMap.view,
            parentTestTargetId: targetServiceId,
            params: JSON.stringify(viewParams),
          },
        ]
      );
      const normalized = ensureTargetType(normalizeTargets(created), "view");
      setExistingTargets((prev) => [...prev, ...normalized]);
      return created[0] ?? null;
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Viewの作成に失敗しました";
      setSelectionError(message);
      return null;
    }
  };

  const createService = async (
    serviceName: string,
    onCreated: (serviceId: string) => void
  ) => {
    if (!apiClient) {
      setSelectionError("APIクライアントが初期化されていません");
      return;
    }
    const typeId = typeMap.service;
    if (!typeId) {
      setSelectionError("Serviceタイプの取得に失敗しています");
      return;
    }
    try {
      const created = await apiClient.createTestTargets(workspaceId, projectId, [
        {
          name: serviceName || "新規Service",
          displayOrder: 1,
          typeId,
          parentTestTargetId: undefined,
          params: JSON.stringify({ status: "need_review" }),
        },
      ]);
      const normalized = ensureTargetType(normalizeTargets(created), "service");
      setExistingTargets((prev) => [...prev, ...normalized]);
      onCreated(created[0].testTargetId);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Serviceの作成に失敗しました";
      setSelectionError(message);
    }
  };

  const createServiceDirect = useCallback(
    async (
      serviceName: string,
      options?: { suppressSelectionError?: boolean }
    ): Promise<GraphQLTestTarget | null> => {
      if (!apiClient) {
        if (!options?.suppressSelectionError) {
          setSelectionError("APIクライアントが初期化されていません");
        }
        return null;
      }
      const typeId = typeMap.service;
      if (!typeId) {
        if (!options?.suppressSelectionError) {
          setSelectionError("Serviceタイプの取得に失敗しています");
        }
        return null;
      }
      try {
        const created = await apiClient.createTestTargets(workspaceId, projectId, [
          {
            name: serviceName || "新規Service",
            displayOrder: 1,
            typeId,
            parentTestTargetId: undefined,
            params: JSON.stringify({ status: "need_review" }),
          },
        ]);
        const normalized = ensureTargetType(normalizeTargets(created), "service");
        setExistingTargets((prev) => [...prev, ...normalized]);
        return created[0] ?? null;
      } catch (err: unknown) {
        if (!options?.suppressSelectionError) {
          const message =
            err instanceof Error ? err.message : "Serviceの作成に失敗しました";
          setSelectionError(message);
        }
        return null;
      }
    },
    [apiClient, ensureTargetType, normalizeTargets, projectId, typeMap, workspaceId]
  );

  const createViewsBulk = useCallback(
    async (
      serviceId: string,
      viewData: Array<{ name: string; path?: string }>,
      options?: { suppressSelectionError?: boolean }
    ): Promise<GraphQLTestTarget[]> => {
      if (!apiClient) {
        if (!options?.suppressSelectionError) {
          setSelectionError("APIクライアントが初期化されていません");
        }
        return [];
      }
      if (!typeMap.view) {
        if (!options?.suppressSelectionError) {
          setSelectionError("Viewタイプの取得に失敗しています");
        }
        return [];
      }
      const payload = viewData.map((data, index) => {
        const viewParams: any = { status: "need_review" };
        
        // path情報がある場合はpathsに追加
        if (data.path) {
          viewParams.paths = [{ path: data.path }];
        }
        
        return {
          name: data.name || "新規View",
          displayOrder: index + 1,
          typeId: typeMap.view,
          parentTestTargetId: serviceId,
          params: JSON.stringify(viewParams),
        };
      });
      try {
      const created = await apiClient.createTestTargets(
        workspaceId,
        projectId,
        payload
      );
      const normalized = ensureTargetType(normalizeTargets(created), "view");
      setExistingTargets((prev) => [...prev, ...normalized]);
      
      // 作成されたViewデータをキャッシュに追加
      if (apiClient) {
        try {
          const allTargets = await apiClient.getTestTargets(workspaceId, projectId);
          const viewCache = new Map<string, GraphQLTestTarget>();
          allTargets.forEach((target) => {
            if (target.type?.name?.toLowerCase() === "view") {
              viewCache.set(target.testTargetId, target);
            }
          });
          setViewDataCache((prev) => {
            const merged = new Map(prev);
            viewCache.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        } catch (error) {
          console.warn("Viewデータキャッシュの更新に失敗:", error);
        }
      }
      
      return created;
      } catch (err: unknown) {
        if (!options?.suppressSelectionError) {
          const message =
            err instanceof Error ? err.message : "Viewの作成に失敗しました";
          setSelectionError(message);
        }
        return [];
      }
    },
    [apiClient, ensureTargetType, normalizeTargets, projectId, typeMap, workspaceId]
  );

  const createItemsBulk = useCallback(
    async (
      viewId: string,
      itemNames: string[],
      options?: { suppressSelectionError?: boolean }
    ): Promise<GraphQLTestTarget[]> => {
      if (!apiClient) {
        if (!options?.suppressSelectionError) {
          setSelectionError("APIクライアントが初期化されていません");
        }
        return [];
      }
      if (!typeMap.item) {
        if (!options?.suppressSelectionError) {
          setSelectionError("Itemタイプの取得に失敗しています");
        }
        return [];
      }
      const itemParams = buildDefaultItemParams();
      const payload = itemNames.map((name, index) => ({
        name: name || "新規Item",
        displayOrder: index + 1,
        typeId: typeMap.item,
        parentTestTargetId: viewId,
        params: JSON.stringify(itemParams),
      }));
      try {
        const created = await apiClient.createTestTargets(
          workspaceId,
          projectId,
          payload
        );
        const normalized = ensureTargetType(normalizeTargets(created), "item");
        setExistingTargets((prev) => [...prev, ...normalized]);
        return created;
      } catch (err: unknown) {
        if (!options?.suppressSelectionError) {
          const message =
            err instanceof Error ? err.message : "Itemの作成に失敗しました";
          setSelectionError(message);
        }
        return [];
      }
    },
    [apiClient, ensureTargetType, normalizeTargets, projectId, typeMap, workspaceId]
  );

  const handleBulkCreateViews = useCallback(async () => {
    const targets = viewMatches.filter((match) => match.action === "bulk_create");
    if (targets.length === 0) {
      return;
    }
    setSelectionError(null);
    setBulkProcessingViews(true);

    const failedViewNames: string[] = [];
    let createdServiceCount = 0;
    let createdViewCount = 0;

    try {
      const updatedMatches = [...viewMatches];
      const defaultParent = getDefaultParentServiceForBulkCreate();
      const grouped = new Map<
        string,
        { serviceId: string | null; items: ViewMatchResolution[] }
      >();

      targets.forEach((match) => {
        const parentServiceId = match.parentServiceId ?? defaultParent ?? null;
        const key = parentServiceId || "__temp__";
        const current = grouped.get(key) ?? { serviceId: parentServiceId, items: [] };
        current.items.push(match);
        grouped.set(key, current);
      });

      const groups = Array.from(grouped.values());
      for (const group of groups) {
        let serviceId = group.serviceId;
        if (!serviceId) {
          const createdService = await createServiceDirect(TEMP_SERVICE_NAME, {
            suppressSelectionError: true,
          });
          if (!createdService) {
            group.items.forEach((item) => {
              failedViewNames.push(item.graph_node_name);
              const targetIndex = updatedMatches.findIndex(
                (m) => m.graph_node_id === item.graph_node_id
              );
              if (targetIndex !== -1) {
                updatedMatches[targetIndex] = {
                  ...updatedMatches[targetIndex],
                  action: "skipped",
                  reason: "生成に失敗したためスキップします",
                };
              }
            });
            continue;
          }
          serviceId = createdService.testTargetId;
          createdServiceCount++;
        }

        for (const item of group.items) {
          const viewData = [{ name: item.graph_node_name, path: item.path }];
          const createdViews = await createViewsBulk(serviceId!, viewData, {
            suppressSelectionError: true,
          });
          const created = createdViews[0];
          if (!created) {
            failedViewNames.push(item.graph_node_name);
            const targetIndex = updatedMatches.findIndex(
              (m) => m.graph_node_id === item.graph_node_id
            );
            if (targetIndex !== -1) {
              updatedMatches[targetIndex] = {
                ...updatedMatches[targetIndex],
                action: "skipped",
                reason: "生成に失敗したためスキップします",
              };
            }
            continue;
          }
          createdViewCount++;
          const targetIndex = updatedMatches.findIndex(
            (m) => m.graph_node_id === item.graph_node_id
          );
          if (targetIndex !== -1) {
            updatedMatches[targetIndex] = {
              ...updatedMatches[targetIndex],
              action: "existing",
              view_test_target_id: created.testTargetId,
              view_name: created.name,
              parentServiceId: serviceId || undefined,
              confidence: "manual",
              reason: "一括新規登録",
            };
          }
          if (item.path) {
            const updateKey = `${created.testTargetId}:${item.path}`;
            setUpdatedViewPaths((prev) => {
              const merged = new Set(prev);
              merged.add(updateKey);
              return merged;
            });
          }
        }
      }

      setViewMatches(updatedMatches);

      if (failedViewNames.length > 0) {
        setSelectionError(
          `以下のViewの作成に失敗しました: ${failedViewNames.join(", ")}`
        );
      }

      if (createdViewCount > 0 && apiClient) {
        try {
          const allTargets = await apiClient.getTestTargets(workspaceId, projectId);
          const viewCache = new Map<string, GraphQLTestTarget>();
          allTargets.forEach((target) => {
            if (target.type?.name?.toLowerCase() === "view") {
              viewCache.set(target.testTargetId, target);
            }
          });
          setViewDataCache((prev) => {
            const merged = new Map(prev);
            viewCache.forEach((value, key) => merged.set(key, value));
            return merged;
          });
        } catch (err) {
          console.warn("Viewデータキャッシュの更新に失敗:", err);
        }
      }

      const totalCreated = createdServiceCount + createdViewCount;
      if (totalCreated > 0 && matchResult?.trace_id) {
        try {
          await operationLogService.recordBillingLog({
            traceId: matchResult.trace_id,
            projectId,
            workspaceId,
            billableUnitCount: totalCreated,
            feature: "transitionGraph",
            message: `Service/Viewの一括作成が完了しました（Service: ${createdServiceCount}個, View: ${createdViewCount}個）`,
          });
        } catch (err) {
          console.warn("課金ログの出力に失敗:", err);
        }
      }
    } finally {
      setBulkProcessingViews(false);
    }
  }, [
    TEMP_SERVICE_NAME,
    createServiceDirect,
    createViewsBulk,
    getDefaultParentServiceForBulkCreate,
    viewMatches,
    matchResult,
    projectId,
    workspaceId,
    apiClient,
  ]);

  const handleBulkCreateViewsAndGenerate = async () => {
    if (bulkProcessingViews) {
      return;
    }
    await handleBulkCreateViews();
    // 一括作成完了後、警告を表示してユーザーに再生成を促す
    setIsViewChanged(true);
  };

  // 既存Viewのpathを更新する処理
  const handleUpdateViewPaths = useCallback(async () => {
    if (!apiClient) {
      setSelectionError("APIクライアントが初期化されていません");
      return;
    }

    setBulkProcessingViews(true);
    setSelectionError(null);

    try {
      // 更新対象のViewを抽出
      const updateTargets = viewMatches.filter((match) => {
        return match.action === "existing" && match.view_test_target_id && match.path;
      });

      if (updateTargets.length === 0) {
        setSelectionError("更新対象のViewが見つかりませんでした");
        return;
      }

      // 既存ターゲットを取得（params情報を含む）
      const allTargets = await apiClient.getTestTargets(workspaceId, projectId);

      // 各Viewのpathを更新
      let updatedCount = 0;
      const newUpdatedPaths = new Set<string>();
      
      for (const match of updateTargets) {
        if (!match.view_test_target_id || !match.path) {
          continue;
        }

        // 既存Viewを取得
        const existingView = allTargets.find((t) => t.testTargetId === match.view_test_target_id);
        if (!existingView) {
          continue;
        }

        // 既存のparamsを取得
        let existingParams: any = {};
        try {
          existingParams = existingView.params
            ? typeof existingView.params === "string"
              ? JSON.parse(existingView.params)
              : existingView.params
            : {};
        } catch (e) {
          console.warn("既存paramsの解析に失敗:", e);
          existingParams = {};
        }

        // 既存のpathsを取得
        const currentPaths = getViewPaths(existingParams);
        // ノードのpathが既存のpathsに含まれているかチェック
        const hasPath = currentPaths.some((p) => p.path === match.path);
        if (hasPath) {
          continue; // 既に存在する場合はスキップ
        }

        // 新しいpathを追加
        const updatedPaths = [...currentPaths, { path: match.path }];
        const updatedParams = setViewPaths(existingParams, updatedPaths);

        // Viewを更新
        await apiClient.updateTestTarget(workspaceId, projectId, existingView.testTargetId, {
          params: updatedParams,
        });

        // 更新済みとして記録
        const updateKey = `${match.view_test_target_id}:${match.path}`;
        newUpdatedPaths.add(updateKey);
        updatedCount++;
      }
      
      // 更新済みのViewを状態に追加
      if (newUpdatedPaths.size > 0) {
        setUpdatedViewPaths((prev) => {
          const merged = new Set(prev);
          newUpdatedPaths.forEach((key) => merged.add(key));
          return merged;
        });
      }

      if (updatedCount === 0) {
        setSelectionError("更新対象のViewが見つかりませんでした（既にpathが登録されている可能性があります）");
        return;
      }

      // 既存ターゲットを再取得して状態を更新
      const updatedTargets = await apiClient.getTestTargets(workspaceId, projectId);
      const normalized = updatedTargets.map((t) => ({
        id: t.testTargetId,
        name: t.name,
        type: t.type?.name || "",
        parentId: t.parentTestTargetId,
      }));
      setExistingTargets(normalized);
      
      // Viewデータをキャッシュに更新
      const viewCache = new Map<string, GraphQLTestTarget>();
      updatedTargets.forEach((target) => {
        if (target.type?.name?.toLowerCase() === "view") {
          viewCache.set(target.testTargetId, target);
        }
      });
      setViewDataCache(viewCache);

      console.log(`${updatedCount}個のViewのpathを更新しました`);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Viewのpath更新に失敗しました";
      setSelectionError(message);
      console.error("View path更新エラー:", err);
    } finally {
      setBulkProcessingViews(false);
    }
  }, [apiClient, viewMatches, workspaceId, projectId, setExistingTargets]);


  const handleBulkCreateItems = useCallback(async () => {
    const targets = transitionResolutions
      .map((trans, index) => ({ trans, index }))
      .filter(({ trans }) => trans.action === "bulk_create");
    if (targets.length === 0) {
      return;
    }
    setSelectionError(null);
    setBulkProcessingItems(true);

    const failedItemNames: string[] = [];
    let createdItemCount = 0;

    try {
      const updatedTransitions = [...transitionResolutions];
      let hasMissingView = false;

      for (const { trans, index } of targets) {
        const viewId = resolveViewIdForTransition(trans);
        if (!viewId) {
          setSelectionError("遷移元Viewが不明な遷移があるため一括作成できません");
          hasMissingView = true;
          break;
        }

        const createdItems = await createItemsBulk(viewId, [trans.item_name], {
          suppressSelectionError: true,
        });
        const created = createdItems[0];
        if (!created) {
          failedItemNames.push(trans.item_name);
          updatedTransitions[index] = {
            ...updatedTransitions[index],
            action: "skipped",
            reason: "生成に失敗したためスキップします",
          };
          continue;
        }
        createdItemCount++;
        updatedTransitions[index] = {
          ...updatedTransitions[index],
          action: "existing",
          resolved_item_test_target_id: created.testTargetId,
          resolved_item_name: created.name,
          confidence: "manual",
          reason: "一括新規登録",
        };
      }

      if (hasMissingView) {
        return;
      }

      setTransitionResolutions(updatedTransitions);

      if (failedItemNames.length > 0) {
        setSelectionError(
          `以下のItemの作成に失敗しました: ${failedItemNames.join(", ")}`
        );
      }

      // 課金ログを出力（Itemの作成数）
      if (createdItemCount > 0 && matchResult?.trace_id) {
        try {
          await operationLogService.recordBillingLog({
            traceId: matchResult.trace_id,
            projectId,
            workspaceId,
            billableUnitCount: createdItemCount,
            feature: "transitionGraph",
            message: `Itemの一括作成が完了しました（${createdItemCount}個）`,
          });
        } catch (err) {
          console.warn("課金ログの出力に失敗:", err);
        }
      }
    } finally {
      setBulkProcessingItems(false);
    }
  }, [
    createItemsBulk,
    resolveViewIdForTransition,
    transitionResolutions,
    matchResult,
    projectId,
    workspaceId,
  ]);

  const createItem = async (params?: {
    serviceId?: string;
    viewId?: string;
    itemName?: string;
  }): Promise<GraphQLTestTarget | null> => {
    if (!apiClient) {
      setSelectionError("APIクライアントが初期化されていません");
      return null;
    }
    if (!typeMap.item) {
      setSelectionError("Itemタイプの取得に失敗しています");
      return null;
    }
    const targetServiceId = params?.serviceId ?? selectedServiceIdForItem;
    const targetViewId = params?.viewId ?? selectedViewIdForItem;
    if (!targetServiceId || !targetViewId) {
      setSelectionError("ServiceとViewを選択してください");
      return null;
    }

    try {
      const itemParams = buildDefaultItemParams();
      const targetItemName = (params?.itemName ?? newItemName) || "新規Item";
      const created = await apiClient.createTestTargets(workspaceId, projectId, [
        {
          name: targetItemName,
          displayOrder: 1,
          typeId: typeMap.item,
          parentTestTargetId: targetViewId,
          params: JSON.stringify(itemParams),
        },
      ]);
      const normalized = ensureTargetType(normalizeTargets(created), "item");
      setExistingTargets((prev) => [...prev, ...normalized]);
      return created[0] ?? null;
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Itemの作成に失敗しました";
      setSelectionError(message);
      return null;
    }
  };

  const findTargetName = useCallback(
    (targetId?: string) => {
      if (!targetId) {
        return "";
      }
      const found = existingTargets.find((target) => target.id === targetId);
      return found?.name || targetId;
    },
    [existingTargets]
  );

  const findServiceNameById = useCallback(
    (serviceId?: string | null) => {
      if (!serviceId) {
        return "";
      }
      const found = targetById.get(serviceId);
      if (found?.name) {
        return found.name;
      }
      return serviceId;
    },
    [targetById]
  );

  // 登録実行処理
  const handleRegister = async (options?: {
    skipMissingItemCheck?: boolean;
    suppressEmptyRegisteredError?: boolean;
  }) => {
    const skipMissingItemCheck = options?.skipMissingItemCheck ?? false;
    const suppressEmptyRegisteredError =
      options?.suppressEmptyRegisteredError ?? false;
    const registerTargets = transitionResolutions
      .map((trans, index) => ({ trans, index }))
      .filter(({ trans }) => trans.action !== "skipped");

    if (registerTargets.length === 0) {
      setError("登録対象の遷移情報がありません（スキップ以外を設定してください）");
      return;
    }

    if (!skipMissingItemCheck) {
      const missingItem = registerTargets.find(
        ({ trans }) =>
          !(trans.resolved_item_test_target_id ?? trans.item_test_target_id)
      );
      if (missingItem) {
        setError("Itemが未確定の遷移があります。新規登録または既存選択をしてください");
        return;
      }
    }

    setRegistering(true);
    setError(null);

    try {
      // 選択された遷移情報のみを登録形式に変換
      const transitions = registerTargets.map(({ trans }) => ({
        item_test_target_id:
          trans.resolved_item_test_target_id ?? trans.item_test_target_id ?? "",
        transitions: [
          {
            targetId: trans.target_view_id,
            status: "need_review" as const,
          },
        ],
      }));

      const result = await transitionGraphService.registerTransitions({
        transitions,
        workspaceId,
        projectId,
        traceId: matchResult?.trace_id,
        traceparent: matchResult?.traceparent,
      });

      if (!result.registered_count && !suppressEmptyRegisteredError) {
        setError("遷移情報の登録対象が見つかりませんでした");
      }

      // 登録結果サマリを表示するため state に保存（閉じるで完了通知）
      setTransitionRegistrationResult(result);
    } catch (error: any) {
      console.error("遷移情報登録エラー:", error);
      setError(`遷移情報登録に失敗しました: ${error.message}`);
    } finally {
      setRegistering(false);
    }
  };

  // ダイアログクローズ処理
  const handleClose = () => {
    setGraphText("");
    setError(null);
    setLoading(false);
    setMatchResult(null);
    setRegistering(false);
    setTransitionRegistrationResult(null);
    setFileError(null);
    setIsDragOver(false);
    setUploadedFileName(null);
    onClose();
  };

  // 遷移登録結果サマリ表示後に閉じる（親に完了通知してから閉じる）
  const handleCloseAfterTransitionSummary = () => {
    onRegistrationComplete();
    setTransitionRegistrationResult(null);
    setRegistering(false);
    setError(null);
    onClose();
  };

  // 信頼度の色を取得
  const getConfidenceColor = (confidence: string) => {
    switch (confidence.toLowerCase()) {
      case "high":
        return "success";
      case "medium":
        return "warning";
      case "low":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: "600px" },
        }}
      >
        {transitionRegistrationResult ? (
          <>
            <DialogTitle>登録結果</DialogTitle>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2}>
                <Alert
                  severity={
                    (transitionRegistrationResult.failed_items?.length ?? 0) > 0
                      ? "warning"
                      : "success"
                  }
                >
                  {transitionRegistrationResult.message}
                </Alert>
                <Typography variant="subtitle2">
                  成功: {transitionRegistrationResult.registered_count} 件
                  {(transitionRegistrationResult.failed_items?.length ?? 0) > 0 && (
                    <> / 失敗: {transitionRegistrationResult.failed_items!.length} 件</>
                  )}
                </Typography>
                {(transitionRegistrationResult.failed_items?.length ?? 0) > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="error" gutterBottom>
                      失敗したItem:
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                      <Table size="small" aria-label="失敗したItem一覧">
                        <TableHead>
                          <TableRow>
                            <TableCell variant="head" sx={{ fontWeight: 600 }}>
                              Item名
                            </TableCell>
                            <TableCell variant="head" sx={{ fontWeight: 600 }}>
                              ID
                            </TableCell>
                            <TableCell variant="head" sx={{ fontWeight: 600 }}>
                              遷移元
                            </TableCell>
                            <TableCell variant="head" sx={{ fontWeight: 600 }}>
                              遷移先
                            </TableCell>
                            <TableCell variant="head" sx={{ fontWeight: 600 }}>
                              理由
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {transitionRegistrationResult.failed_items!.map((item, idx) => {
                            const trans = transitionResolutions.find(
                              (t) =>
                                (t.resolved_item_test_target_id ?? t.item_test_target_id) ===
                                item.item_test_target_id
                            );
                            const itemName = trans?.resolved_item_name ?? trans?.item_name;
                            const sourceLabel =
                              trans?.source_view_name ??
                              trans?.source_view_graph_node_id ??
                              trans?.source_view_test_target_id ??
                              "—";
                            const targetLabel =
                              trans?.target_view_name ?? trans?.target_view_id ?? "—";
                            return (
                              <TableRow key={idx}>
                                <TableCell>{itemName ?? "—"}</TableCell>
                                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                                  {item.item_test_target_id}
                                </TableCell>
                                <TableCell>{sourceLabel}</TableCell>
                                <TableCell>{targetLabel}</TableCell>
                                <TableCell>{item.reason}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseAfterTransitionSummary} variant="contained">
                閉じる
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AccountTree color="primary" />
            遷移情報の一括登録
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3}>
            {/* エラー表示 */}
            {(error || selectionError) && (
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  backgroundColor: (theme) => theme.palette.background.paper,
                  pt: 1,
                  pb: 1,
                }}
              >
                {/* NOTE: スクロール時も見えるように固定表示 */}
                <Stack spacing={1}>
                  {error && (
                    <Alert severity="error" onClose={() => setError(null)}>
                      {error}
                    </Alert>
                  )}
                  {selectionError && (
                    <Alert
                      severity="warning"
                      onClose={() => setSelectionError(null)}
                    >
                      {selectionError}
                    </Alert>
                  )}
                </Stack>
              </Box>
            )}

            {/* グラフテキスト入力エリア */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Mermaid形式のグラフテキスト
              </Typography>
            <Stack spacing={1} sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Markdownファイルをドロップまたはクリックして選択できます。貼り付けも可能です。
              </Typography>
              <Paper
                variant="outlined"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={handleFileButtonClick}
                sx={{
                  borderStyle: "dashed",
                  borderWidth: 2,
                  borderColor: isDragOver ? "primary.main" : "grey.300",
                  backgroundColor: isDragOver ? "action.hover" : "background.paper",
                  p: 4,
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <input
                  type="file"
                  accept=".md"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
                {uploadedFileName ? (
                  <Stack spacing={0.5} alignItems="center">
                    <Description sx={{ fontSize: 48, color: "success.main" }} />
                    <Typography variant="h6" color="success.main">
                      {uploadedFileName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      解析済みのファイル
                    </Typography>
                  </Stack>
                ) : (
                  <Stack spacing={0.5} alignItems="center">
                    <CloudUpload
                      sx={{ fontSize: 48, color: "text.secondary", mb: 0.5 }}
                    />
                    <Typography variant="h6" color="text.secondary">
                      Markdownファイルをドロップまたはクリックして選択
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Mermaidの「graph TD」から始まる内容を含む.mdのみアップロードできます
                    </Typography>
                  </Stack>
                )}
              </Paper>
            </Stack>
            {fileError && (
              <Alert
                severity="warning"
                sx={{ mb: 1 }}
                onClose={() => setFileError(null)}
              >
                {fileError}
              </Alert>
            )}
              <TextField
                multiline
                rows={10}
                fullWidth
                value={graphText}
                onChange={(e) => setGraphText(e.target.value)}
                placeholder="graph TD&#10;    A[画面A] -->|ボタン1| B[画面B]&#10;    A -->|ボタン2| C[画面C]"
                variant="outlined"
                disabled={loading || registering}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Mermaid形式のグラフテキストを貼り付けてください
              </Typography>
            </Box>

            {/* マッチング結果プレビュー */}
            {matchResult && (
              <Box display="flex" flexDirection="column" gap={2}>
                <Typography variant="h6" gutterBottom>
                  マッチング結果
                </Typography>

                {/* ノードマッチング結果 */}
                <Box sx={{ mb: 1 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                    spacing={2}
                  >
                    <Typography variant="subtitle1">
                      ノードとViewのマッチング ({viewMatches.length}件)
                    </Typography>
                    <Tooltip title="未マッチのViewはここで新規作成または既存に紐付けしてください">
                      <Chip label="Step 1: View確定" color="primary" size="small" />
                    </Tooltip>
                  </Stack>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#e5e7eb' }}>
                          <TableCell>グラフノード</TableCell>
                          <TableCell>マッチしたView</TableCell>
                          <TableCell>マッチしたViewの親Service</TableCell>
                          <TableCell>信頼度</TableCell>
                          <TableCell>理由</TableCell>
                          <TableCell>対応</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {viewMatches.map((match) => (
                          <TableRow key={match.graph_node_id}>
                            <TableCell>{match.graph_node_name}</TableCell>
                            <TableCell>
                              {match.view_name || (
                                <Typography variant="body2" color="error">
                                  未マッチ
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {match.view_test_target_id
                                ? findServiceNameById(
                                    findServiceIdForTarget(
                                      match.view_test_target_id
                                    ) ||
                                      match.parentServiceId ||
                                      ""
                                  )
                                : match.action === "bulk_create"
                                ? match.parentServiceId
                                  ? findServiceNameById(match.parentServiceId)
                                  : TEMP_SERVICE_NAME
                                : (
                                  <Typography variant="body2" color="text.secondary">
                                    -
                                  </Typography>
                                )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={match.confidence}
                                color={getConfidenceColor(
                                  match.confidence
                                ) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontSize="0.75rem">
                                {match.reason}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ minWidth: 180 }}>
                              <FormControl fullWidth size="small">
                                <Select
                                  value={match.action}
                                  onChange={(e) =>
                                    handleViewActionChange(
                                      match.graph_node_id,
                                      e.target.value as ViewAction
                                    )
                                  }
                                >
                                  <MenuItem value="bulk_create">一括新規登録</MenuItem>
                                  <MenuItem value="manual">手動で選択</MenuItem>
                                  <MenuItem value="skipped">スキップ</MenuItem>
                                  <MenuItem
                                    value="existing"
                                    disabled={!match.view_test_target_id}
                                  >
                                    分析結果を使用
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  {hasBulkCreateViews ? (
                    <Alert severity="warning" variant="outlined" sx={{ py: 0.5, px: 1.5 }}>
                      一括登録後に再生成が必要です。
                    </Alert>
                  ) : (
                    isViewChanged && (
                      <Alert severity="warning" variant="outlined" sx={{ py: 0.5, px: 1.5 }}>
                        ノードとViewのマッチングが変更されました。再生成が必要です。
                      </Alert>
                    )
                  )}
                  {hasUpdatablePaths && !hasBulkCreateViews && (
                    <Alert severity="info" variant="outlined" sx={{ py: 0.5, px: 1.5 }}>
                      更新できるpathがあります
                    </Alert>
                  )}
                  {loadingTargets && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CircularProgress size={18} />
                      <Typography variant="body2">
                        ターゲット情報更新中
                      </Typography>
                    </Stack>
                  )}
                  {hasUpdatablePaths && !hasBulkCreateViews && (
                    <Button
                      onClick={() => void handleUpdateViewPaths()}
                      variant="outlined"
                      disabled={
                        loading ||
                        bulkProcessingViews ||
                        registering
                      }
                      startIcon={
                        bulkProcessingViews ? (
                          <CircularProgress size={16} />
                        ) : (
                          <SwapHoriz />
                        )
                      }
                    >
                      {bulkProcessingViews ? "更新中..." : "分析結果でpathを更新"}
                    </Button>
                  )}
                  <Button
                    onClick={
                      hasBulkCreateViews
                        ? handleBulkCreateViewsAndGenerate
                        : handleGenerateTransitions
                    }
                    variant="contained"
                    startIcon={
                      regenCooldown || generatingTransitions || bulkProcessingViews ? (
                        <CircularProgress size={16} />
                      ) : (
                        <SwapHoriz />
                      )
                    }
                    disabled={
                      loading ||
                      generatingTransitions ||
                      bulkProcessingViews ||
                      regenCooldown ||
                      viewMatches.length === 0 ||
                      registering
                    }
                  >
                    {regenCooldown
                      ? "登録中..."
                      : hasBulkCreateViews
                      ? "Viewを一括作成"
                      : "遷移情報を再生成"}
                  </Button>
                </Stack>

                <Divider />

                {/* 遷移情報 */}
                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                    spacing={2}
                  >
                    <Typography variant="subtitle1">
                      遷移情報 ({transitionResolutions.length}件 / 登録対象:{" "}
                      {transitionResolutions.filter((t) => t.action !== "skipped").length}件)
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Tooltip title="未マッチのItemはここで新規作成または既存に紐付けしてください">
                        <Chip
                          label="Step 2: Item確定"
                          color="primary"
                          size="small"
                        />
                      </Tooltip>
                    </Stack>
                  </Stack>
                  {generatingTransitions && (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      p={2}
                      gap={1}
                    >
                      <CircularProgress size={20} />
                      <Typography variant="body2">
                        遷移情報を再計算しています...
                      </Typography>
                    </Box>
                  )}
                  {!generatingTransitions && transitionResolutions.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#e5e7eb' }}>
                            <TableCell>Item名</TableCell>
                            <TableCell>遷移元</TableCell>
                            <TableCell>遷移先</TableCell>
                            <TableCell>信頼度</TableCell>
                            <TableCell>理由</TableCell>
                            <TableCell>対応</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {transitionResolutions.map((trans, index) => (
                            <TableRow key={`${trans.item_name}-${index}`}>
                              <TableCell>
                                <Stack spacing={0.5}>
                                  <Typography variant="body2">
                                    {trans.resolved_item_name || trans.item_name}
                                  </Typography>
                                  {trans.resolved_item_test_target_id &&
                                    trans.resolved_item_test_target_id !==
                                      trans.item_test_target_id && (
                                      <Chip
                                        size="small"
                                        label="手動選択"
                                        color="warning"
                                      />
                                    )}
                                </Stack>
                              </TableCell>
                            <TableCell>
                              {trans.source_view_name || (
                                <Typography variant="body2" color="text.secondary">
                                  未取得
                                </Typography>
                              )}
                              </TableCell>
                              <TableCell>{trans.target_view_name}</TableCell>
                              <TableCell>
                                <Chip
                                  label={trans.confidence}
                                  color={getConfidenceColor(
                                    trans.confidence
                                  ) as any}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontSize="0.75rem">
                                  {trans.reason}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ minWidth: 200 }}>
                                <FormControl fullWidth size="small">
                                  <Select
                                    value={trans.action}
                                    onChange={(e) =>
                                      handleItemActionChange(
                                        index,
                                        e.target.value as ItemAction
                                      )
                                    }
                                  >
                                  <MenuItem value="bulk_create">一括新規登録</MenuItem>
                                  <MenuItem value="manual">手動で選択</MenuItem>
                                    <MenuItem value="skipped">スキップ</MenuItem>
                                    <MenuItem
                                      value="existing"
                                      disabled={
                                        !(
                                          trans.resolved_item_test_target_id ||
                                          trans.item_test_target_id
                                        )
                                      }
                                    >
                                      分析結果を使用
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    !generatingTransitions && (
                      <Alert severity="warning">
                        新規の遷移情報はありません
                      </Alert>
                    )
                  )}
                </Box>
              </Box>
            )}

            {/* ローディング表示 */}
            {loading && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                p={3}
              >
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                  マッチング処理中...
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading || registering}>
            キャンセル
          </Button>
          {!matchResult && (
            <Button
              onClick={handleMatch}
              variant="outlined"
              disabled={loading || registering || !graphText.trim()}
              startIcon={loading ? <CircularProgress size={16} /> : <AccountTree />}
            >
              マッチング実行
            </Button>
          )}
          {matchResult && transitionResolutions.length > 0 && (
            <>
              {hasBulkCreateItems && (
                <Button
                  onClick={() => void handleBulkCreateItems()}
                  variant="outlined"
                  disabled={
                    loading ||
                    registering ||
                    bulkProcessingItems ||
                    transitionResolutions.every((t) => t.action === "skipped") ||
                    generatingTransitions
                  }
                  startIcon={
                    bulkProcessingItems ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Add />
                    )
                  }
                >
                  {bulkProcessingItems ? "Item作成中..." : "Itemを一括作成"}
                </Button>
              )}
              <Button
                onClick={() => void handleRegister()}
                variant="contained"
                disabled={
                  loading ||
                  registering ||
                  bulkProcessingItems ||
                  transitionResolutions.every((t) => t.action === "skipped") ||
                  generatingTransitions ||
                  hasBulkCreateItems
                }
                startIcon={
                  registering ? (
                    <CircularProgress size={16} />
                  ) : (
                    <CheckCircle />
                  )
                }
              >
                {registering ? "登録中..." : "登録を実行"}
              </Button>
            </>
          )}
        </DialogActions>
          </>
        )}
      </Dialog>

      {/* View手動選択ダイアログ */}
      <Dialog
        open={viewSelectModalOpen}
        onClose={() => {
          setViewSelectModalOpen(false);
          setIsCreatingNewViewInManualSelect(false);
          setManuallyCreatedViewForMatch(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>手動で選択</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 520, pt: 3 }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            グラフノードにマッチさせるViewを選択してください
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="view-select-service-label">Service</InputLabel>
            <Select
              labelId="view-select-service-label"
              label="Service"
              value={selectedServiceIdForView}
              onChange={(e) => {
                const serviceId = e.target.value;
                if (serviceId === "__create_service__") {
                  setServiceCreateReturnTarget("view");
                  setNewServiceNameForView("");
                  setServiceCreateModalOpen(true);
                  return;
                }
                setSelectedServiceIdForView(serviceId);
                const candidates = viewsByService.get(serviceId) || [];
                setSelectedExistingViewId(
                  candidates.length > 0 ? candidates[0].id : ""
                );
                setIsCreatingNewViewInManualSelect(false);
              }}
            >
              {hasServiceType && (
                <MenuItem value="__create_service__">+ 新規サービスを作成</MenuItem>
              )}
              {services.map((service) => (
                <MenuItem key={service.id} value={service.id}>
                  {service.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" disabled={!selectedServiceIdForView}>
            <InputLabel id="view-select-view-label">View</InputLabel>
            <Select
              labelId="view-select-view-label"
              label="View"
              value={selectedExistingViewId}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "__create_view__") {
                  setSelectedExistingViewId(value);
                  setIsCreatingNewViewInManualSelect(true);
                  setManuallyCreatedViewForMatch(null);
                  return;
                }
                setSelectedExistingViewId(value);
                setIsCreatingNewViewInManualSelect(false);
                setManuallyCreatedViewForMatch(null);
              }}
            >
              <MenuItem value="__create_view__">+ 新規Viewを作成</MenuItem>
              {(viewsByService.get(selectedServiceIdForView) || []).map((view) => (
                <MenuItem key={view.id} value={view.id}>
                  {view.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isCreatingNewViewInManualSelect && (
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                登録するView名の名前を入力してください
              </Typography>
              <TextField
                label="View名"
                value={newViewName}
                onChange={(e) => setNewViewName(e.target.value)}
                fullWidth
                size="small"
              />
              {manuallyCreatedViewForMatch && (
                <Typography variant="body2" color="primary.main">
                  「{manuallyCreatedViewForMatch.viewName}」を作成しました。適用を押してマッチング結果に反映してください。
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewSelectModalOpen(false)}>キャンセル</Button>
          {isCreatingNewViewInManualSelect && manuallyCreatedViewForMatch ? (
            <>
              <Button
                onClick={() => {
                  const nodeId = viewSelectionState.nodeId;
                  if (!nodeId) return;
                  applyViewResolution(
                    nodeId,
                    "manual",
                    manuallyCreatedViewForMatch.viewId,
                    manuallyCreatedViewForMatch.viewName,
                    selectedServiceIdForView
                  );
                  setViewSelectionState({ nodeId: null, requestedAction: null });
                  setViewSelectModalOpen(false);
                  setIsCreatingNewViewInManualSelect(false);
                  setManuallyCreatedViewForMatch(null);
                }}
                variant="contained"
              >
                適用
              </Button>
            </>
          ) : isCreatingNewViewInManualSelect ? (
            <Button
              onClick={async () => {
                if (!newViewName.trim()) {
                  setSelectionError("View名を入力してください");
                  return;
                }
                const nodeId = viewSelectionState.nodeId;
                const match = nodeId
                  ? viewMatches.find((m) => m.graph_node_id === nodeId)
                  : null;
                const path = match?.path;
                const created = await createView({
                  viewName: newViewName,
                  path: path,
                });
                if (created) {
                  setManuallyCreatedViewForMatch({
                    viewId: created.testTargetId,
                    viewName:
                      created.name ?? (newViewName.trim() || "新規View"),
                  });
                }
              }}
              variant="contained"
              disabled={!newViewName.trim() || !selectedServiceIdForView}
            >
              Viewを作成
            </Button>
          ) : (
            <Button
              onClick={() => {
                if (selectedExistingViewId) {
                  handleViewSelected(
                    selectedExistingViewId,
                    findTargetName(selectedExistingViewId),
                    selectedServiceIdForView
                  );
                }
              }}
              variant="contained"
              disabled={!selectedExistingViewId}
            >
              適用
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Service新規作成ダイアログ（View作成フローから遷移） */}
      <Dialog
        open={serviceCreateModalOpen}
        onClose={() => {
          setServiceCreateModalOpen(false);
              if (serviceCreateReturnTarget === "item") {
                setItemSelectModalOpen(true);
              } else {
                setViewSelectModalOpen(true);
              }
              setServiceCreateReturnTarget(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Serviceを新規登録</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minWidth: 420,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Service名を入力してください
          </Typography>
          <TextField
            label="Service名"
            value={newServiceNameForView}
            onChange={(e) => setNewServiceNameForView(e.target.value)}
            fullWidth
            size="small"
            placeholder="例) ホテル予約サイト"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setServiceCreateModalOpen(false);
              if (serviceCreateReturnTarget === "item") {
                setItemSelectModalOpen(true);
              } else {
                setViewSelectModalOpen(true);
              }
              setServiceCreateReturnTarget(null);
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={async () => {
              if (!newServiceNameForView.trim()) {
                setSelectionError("Service名を入力してください");
                return;
              }
              await createService(newServiceNameForView, (id) => {
                setSelectedServiceIdForView(id);
                setSelectedServiceIdForItem(id);
                setNewServiceNameForView("");
                setServiceCreateModalOpen(false);
                if (serviceCreateReturnTarget === "item") {
                  setItemSelectModalOpen(true);
                  const viewsForNewService = viewsByService.get(id) || [];
                  setSelectedViewIdForItem(
                    viewsForNewService.length > 0 ? viewsForNewService[0].id : ""
                  );
                  const itemsForFirstView =
                    viewsForNewService.length > 0
                      ? itemsByView.get(viewsForNewService[0].id) || []
                      : [];
                  setSelectedItemIdForExisting(
                    itemsForFirstView.length > 0 ? itemsForFirstView[0].id : ""
                  );
                } else {
                  // TASK: View手動選択に戻る場合、新規Serviceを選択した状態にし、View選択をクリアする
                  setSelectedExistingViewId("");
                  setViewSelectModalOpen(true);
                }
                setServiceCreateReturnTarget(null);
              });
            }}
            variant="contained"
            startIcon={<Add />}
            disabled={!newServiceNameForView.trim()}
          >
            作成して戻る
          </Button>
        </DialogActions>
      </Dialog>

      {/* Item選択ダイアログ */}
      <Dialog
        open={itemSelectModalOpen}
        onClose={() => {
          setItemSelectModalOpen(false);
          setIsCreatingNewItemInManualSelect(false);
          setIsCreatingNewViewInItemSelect(false);
          itemSelectionKeyRef.current = null;
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>手動で選択</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 520, pt: 3 }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            遷移情報を登録するItemを選択してください
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="item-select-service-label">Service</InputLabel>
            <Select
              labelId="item-select-service-label"
              label="Service"
              value={selectedServiceIdForItem}
              onChange={(e) => {
                const serviceId = e.target.value;
                setSelectedServiceIdForItem(serviceId);
                const candidateViews = viewsByService.get(serviceId) || [];
                const firstView = candidateViews.length > 0 ? candidateViews[0].id : "";
                setSelectedViewIdForItem(firstView);
                const candidateItems = firstView
                  ? itemsByView.get(firstView) || []
                  : [];
                setSelectedItemIdForExisting(
                  candidateItems.length > 0 ? candidateItems[0].id : ""
                );
                setIsCreatingNewViewInItemSelect(false);
                setIsCreatingNewItemInManualSelect(false);
              }}
            >
              {services.map((service) => (
                <MenuItem key={service.id} value={service.id}>
                  {service.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" disabled={!selectedServiceIdForItem}>
            <InputLabel id="item-select-view-label">View</InputLabel>
            <Select
              labelId="item-select-view-label"
              label="View"
              value={selectedViewIdForItem}
              onChange={(e) => {
                const viewId = e.target.value;
                setSelectedViewIdForItem(viewId);
                const candidateItems = itemsByView.get(viewId) || [];
                setSelectedItemIdForExisting(
                  candidateItems.length > 0 ? candidateItems[0].id : ""
                );
                setIsCreatingNewViewInItemSelect(false);
                setIsCreatingNewItemInManualSelect(false);
              }}
            >
              {(viewsByService.get(selectedServiceIdForItem) || []).map((view) => (
                <MenuItem key={view.id} value={view.id}>
                  {view.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            fullWidth
            size="small"
            disabled={!selectedViewIdForItem}
          >
            <InputLabel id="item-select-item-label">Item</InputLabel>
            <Select
              labelId="item-select-item-label"
              label="Item"
              value={selectedItemIdForExisting}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "__create_item__") {
                  setSelectedItemIdForExisting(value);
                  setIsCreatingNewItemInManualSelect(true);
                  return;
                }
                setSelectedItemIdForExisting(value);
                setIsCreatingNewItemInManualSelect(false);
              }}
            >
              <MenuItem value="__create_item__">+ 新規Itemを作成</MenuItem>
              {(itemsByView.get(selectedViewIdForItem) || []).map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isCreatingNewItemInManualSelect && (
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                登録するItem名を入力してください
              </Typography>
              <TextField
                label="Item名"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                fullWidth
                size="small"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemSelectModalOpen(false)}>キャンセル</Button>
          <Button
            onClick={async () => {
              let targetViewId = selectedViewIdForItem;

              if (isCreatingNewItemInManualSelect) {
                if (!newItemName.trim()) {
                  setSelectionError("Item名を入力してください");
                  return;
                }
                if (!targetViewId) {
                  setSelectionError("Viewを選択してください");
                  return;
                }
                // NOTE: effectで配列が並び替わるため、indexではなくkeyで適用先を特定する
                const keyRef = itemSelectionKeyRef.current;
                const createdItem = await createItem({
                  serviceId: selectedServiceIdForItem,
                  viewId: targetViewId,
                  itemName: newItemName,
                });
                if (createdItem && keyRef) {
                  const current = transitionResolutionsRef.current;
                  const indicesWithSameKey = current
                    .map((t, i) => (buildTransitionKey(t) === keyRef.key ? i : -1))
                    .filter((i) => i >= 0);
                  const indexForApply =
                    indicesWithSameKey[keyRef.indexInKeyGroup] ??
                    indicesWithSameKey[0];
                  if (indexForApply !== undefined) {
                    applyItemResolution(
                      indexForApply,
                      "manual",
                      createdItem.testTargetId,
                      createdItem.name ?? (newItemName.trim() || "新規Item")
                    );
                  }
                  itemSelectionKeyRef.current = null;
                  setItemSelectionState({ index: null, requestedAction: null });
                  setItemSelectModalOpen(false);
                  setIsCreatingNewItemInManualSelect(false);
                }
                return;
              }

              if (selectedItemIdForExisting) {
                handleItemSelected(
                  selectedItemIdForExisting,
                  findTargetName(selectedItemIdForExisting)
                );
              }
            }}
            variant="contained"
            disabled={
              !selectedServiceIdForItem ||
              (!isCreatingNewViewInItemSelect && !selectedViewIdForItem) ||
              (isCreatingNewViewInItemSelect && !newViewName.trim()) ||
              (isCreatingNewItemInManualSelect && !newItemName.trim()) ||
              (!isCreatingNewItemInManualSelect && !selectedItemIdForExisting)
            }
          >
            適用
          </Button>
        </DialogActions>
      </Dialog>

      {/* Item新規作成ダイアログ */}
      <Dialog
        open={itemCreateModalOpen}
        onClose={() => setItemCreateModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Itemを新規登録</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minWidth: 520,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            ターゲットの登録先を選択してください
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="create-item-service-label">Service</InputLabel>
            <Select
              labelId="create-item-service-label"
              label="Service"
              value={selectedServiceIdForItem}
              onChange={(e) => {
                const serviceId = e.target.value;
                setSelectedServiceIdForItem(serviceId);
                const candidateViews = viewsByService.get(serviceId) || [];
                const firstView = candidateViews.length > 0 ? candidateViews[0].id : "";
                setSelectedViewIdForItem(firstView);
              }}
            >
              <MenuItem value="">
                選択してください
              </MenuItem>
              {services.map((service) => (
                <MenuItem key={service.id} value={service.id}>
                  {service.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" disabled={!selectedServiceIdForItem}>
            <InputLabel id="create-item-view-label">View</InputLabel>
            <Select
              labelId="create-item-view-label"
              label="View"
              value={selectedViewIdForItem}
              onChange={(e) => setSelectedViewIdForItem(e.target.value)}
            >
              <MenuItem value="">
                選択してください
              </MenuItem>
              {(viewsByService.get(selectedServiceIdForItem) || []).map((view) => (
                <MenuItem key={view.id} value={view.id}>
                  {view.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            登録するItemの名前を入力してください
          </Typography>
          <TextField
            label="Item名"
            value={newItemName}
            onChange={(e) => setNewItemName(e.target.value)}
            fullWidth
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemCreateModalOpen(false)}>キャンセル</Button>
          <Button
            onClick={() => void createItem()}
            variant="contained"
            startIcon={<Add />}
            disabled={!selectedServiceIdForItem || !selectedViewIdForItem || !newItemName.trim()}
          >
            作成して適用
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};


